import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

/* ── Mini Excel Mockup ── */
const ExcelMockup: React.FC<{ opacity: number }> = ({ opacity }) => {
  const frame = useCurrentFrame();
  const crossOpacity = interpolate(frame, [55, 70], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const rows = [
    ['Producto A', '$1,200', '15%', '$1,020'],
    ['Producto B', '$890', '10%', '$801'],
    ['Producto C', '$2,450', '?', '#ERR'],
  ];

  return (
    <div style={{ opacity, width: '100%' }}>
      <div
        style={{
          background: '#217346',
          borderRadius: '8px 8px 0 0',
          padding: '6px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
            <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c }} />
          ))}
        </div>
        <span
          style={{
            fontSize: 11,
            color: '#fff',
            fontFamily: 'Inter, system-ui, sans-serif',
            opacity: 0.8,
          }}
        >
          cotizacion_v14_FINAL_final2.xlsx
        </span>
      </div>
      <div
        style={{
          background: '#FFFFFF',
          border: `1px solid #E2E8F0`,
          borderRadius: '0 0 8px 8px',
          padding: 8,
          position: 'relative',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 11,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          <thead>
            <tr>
              {['Producto', 'Precio', 'Desc.', 'Total'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '4px 8px',
                    textAlign: 'left',
                    color: COLORS.gray,
                    fontWeight: 500,
                    borderBottom: `1px solid ${COLORS.grayDark}30`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      padding: '4px 8px',
                      color: cell === '#ERR' ? COLORS.red : COLORS.white,
                      fontWeight: cell === '#ERR' || cell === '?' ? 700 : 400,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Red X overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: crossOpacity,
          }}
        >
          <svg width={60} height={60} viewBox="0 0 60 60">
            <line
              x1={10}
              y1={10}
              x2={50}
              y2={50}
              stroke={COLORS.red}
              strokeWidth={6}
              strokeLinecap="round"
            />
            <line
              x1={50}
              y1={10}
              x2={10}
              y2={50}
              stroke={COLORS.red}
              strokeWidth={6}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

/* ── Competitor Price Mockup ── */
const CompetitorMockup: React.FC<{ opacity: number }> = ({ opacity }) => {
  const frame = useCurrentFrame();
  const alertPulse = Math.sin(frame * 0.15) * 0.3 + 0.7;

  const competitors = [
    { name: 'Competidor A', price: '$980', change: '-12%', ago: 'Hace 2 días' },
    { name: 'Competidor B', price: '$1,050', change: '-5%', ago: 'Hace 5h' },
    { name: 'Tu precio', price: '$1,200', change: '', ago: 'Sin actualizar' },
  ];

  return (
    <div style={{ opacity, width: '100%' }}>
      <div
        style={{
          background: `${COLORS.bgLight}DD`,
          border: `1px solid ${COLORS.amber}30`,
          borderRadius: 8,
          padding: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.amber,
            fontFamily: 'Inter, system-ui, sans-serif',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ opacity: alertPulse }}>⚠️</span> Precios desactualizados
        </div>
        {competitors.map((c, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '5px 0',
              borderTop: i > 0 ? `1px solid ${COLORS.grayDark}20` : 'none',
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: i === 2 ? COLORS.white : COLORS.gray,
                fontWeight: i === 2 ? 600 : 400,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {c.name}
            </span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: i === 2 ? COLORS.red : COLORS.emerald,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {c.price}
              </span>
              {c.change && (
                <span
                  style={{
                    fontSize: 10,
                    color: COLORS.emerald,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {c.change}
                </span>
              )}
              <span
                style={{
                  fontSize: 9,
                  color: COLORS.grayDark,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {c.ago}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Import Black Box Mockup ── */
const ImportMockup: React.FC<{ opacity: number }> = ({ opacity }) => {
  const frame = useCurrentFrame();
  const questionPulse = Math.sin(frame * 0.12) * 0.4 + 0.6;

  return (
    <div style={{ opacity, width: '100%' }}>
      <div
        style={{
          background: `${COLORS.bgLight}DD`,
          border: `1px solid ${COLORS.violet}30`,
          borderRadius: 8,
          padding: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.violet,
            fontFamily: 'Inter, system-ui, sans-serif',
            marginBottom: 8,
          }}
        >
          🚢 Importación #2847
        </div>
        {[
          { label: 'FOB', value: '$12,400' },
          { label: 'Flete', value: '???' },
          { label: 'Seguro', value: '???' },
          { label: 'Arancel', value: '???' },
          { label: 'Costo landed', value: '¿???' },
        ].map((row, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '3px 0',
              borderTop: i > 0 ? `1px solid ${COLORS.grayDark}20` : 'none',
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: COLORS.gray,
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {row.label}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: row.value.includes('?') ? COLORS.amber : COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                opacity: row.value.includes('?') ? questionPulse : 1,
              }}
            >
              {row.value}
            </span>
          </div>
        ))}
        <div
          style={{
            marginTop: 8,
            padding: '4px 8px',
            background: `${COLORS.red}15`,
            border: `1px solid ${COLORS.red}30`,
            borderRadius: 4,
            fontSize: 10,
            color: COLORS.red,
            fontFamily: 'Inter, system-ui, sans-serif',
            textAlign: 'center',
          }}
        >
          Margen real desconocido
        </div>
      </div>
    </div>
  );
};

/* ── Pain Point Card ── */
const PainCard: React.FC<{
  icon: string;
  title: string;
  subtitle: string;
  color: string;
  delay: number;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, color, delay, children }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;

  if (f < 0) return <div style={{ flex: 1, opacity: 0 }} />;

  const scale = spring({ frame: f, fps, config: { damping: 14, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <div
      style={{
        flex: 1,
        background: `${COLORS.bgLight}CC`,
        border: `1px solid ${color}25`,
        borderRadius: 16,
        padding: 20,
        opacity,
        transform: `scale(${scale})`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: `${color}20`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 22,
          }}
        >
          {icon}
        </div>
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: COLORS.white,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            {title}
          </div>
          <div
            style={{ fontSize: 11, color: COLORS.gray, fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            {subtitle}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

const DUR = SCENES.PROBLEM.duration;

export const ProblemScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 15], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 20, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const titleOpacity = interpolate(frame, [5, 25], [0, 1], { extrapolateRight: 'clamp' });
  const titleY = interpolate(frame, [5, 25], [30, 0], { extrapolateRight: 'clamp' });

  const mockup1 = interpolate(frame, [30, 50], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const mockup2 = interpolate(frame, [70, 90], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const mockup3 = interpolate(frame, [110, 130], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="warm" />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 80px',
        }}
      >
        <div
          style={{
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
            textAlign: 'center',
            marginBottom: 32,
          }}
        >
          <div
            style={{
              fontSize: 52,
              fontWeight: 800,
              color: COLORS.white,
              fontFamily: 'Inter, system-ui, sans-serif',
              lineHeight: 1.2,
            }}
          >
            Dejá de <span style={{ color: COLORS.red }}>perder ventas</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, width: '100%' }}>
          <PainCard
            icon="⏰"
            title="Cotizar toma horas"
            subtitle="Tu equipo vive en Excel"
            color={COLORS.red}
            delay={15}
          >
            <ExcelMockup opacity={mockup1} />
          </PainCard>

          <PainCard
            icon="📉"
            title="Precios desactualizados"
            subtitle="Tu competencia ya cambió"
            color={COLORS.amber}
            delay={50}
          >
            <CompetitorMockup opacity={mockup2} />
          </PainCard>

          <PainCard
            icon="🚢"
            title="Importar es una caja negra"
            subtitle="No sabés tu margen real"
            color={COLORS.violet}
            delay={90}
          >
            <ImportMockup opacity={mockup3} />
          </PainCard>
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.PROBLEM} />
    </AbsoluteFill>
  );
};
